import Box from '@mui/material/Box';
import InputLabel from '@mui/material/InputLabel';
import Checkbox from '@mui/material/Checkbox';
import MenuItem from '@mui/material/MenuItem';
import { App } from 'BMapsSrc/BMapsApp';
import { IncrementDecrementInput } from 'BMapsUI/common/IncrementDecrementInput';
import { BasicTextField } from 'BMapsUI/dialog/SimulationPane/Components';
import { TextArea } from 'BMapsUI/common/TextFields';
import Select, { SelectWithInfoTooltip } from 'BMapsUI/common/Select';
import { DialogPrimaryButton } from 'BMapsUI/common/DialogActionButtons';
import { withTooltip, wrapForDisabledTooltip } from 'BMapsUI/common/helpers';
import BMapsTooltip from 'BMapsUI/BMapsTooltip';
import TooltipInfo from 'BMapsUI/common/TooltipInfo';
import { FormControlLabelStyled, PlaceHolder } from 'BMapsUI/dialog/ImportPane/import_components';
import { lookupJobDefinition } from '../../jobRegistry';
// `FieldList` renders a `variant` widget's sub-fields (below), and
// `FieldList.jsx` in turn dispatches to these widgets via `./index` — a
// genuine cycle, but a safe one: the only cross-reference is inside
// `VariantWidget`'s own render, called well after both modules have finished
// loading, never at module-evaluation time (unlike, say, a value computed
// eagerly from a still-loading class — see `jobRegistry.js`'s own
// circular-import fragility for the failure mode this isn't).
import FieldList from '../FieldList';

// Matches ProteinFoldingTab's local CheckboxWithInfoTooltip recipe, so a
// migrated checkbox widget looks identical to its hand-authored predecessor.
const CheckboxWithInfoTooltip = withTooltip(
    ({ label, ...rest }) => (
        <FormControlLabelStyled control={<Checkbox {...rest} />} label={label} />
    ),
    {
        IconWrapperProps: { style: { width: '3rem' } },
        WrapperProps: { style: { alignItems: 'center' } },
    }
);

export function resolve(value, formState) {
    return typeof value === 'function' ? value(formState) : value;
}

// `widgetProps` is reserved for widget-level presentation props (autoFocus,
// sx, onKeyUp, id, ...) — never value/checked/onChange, which always come
// from formState/setFormState.

export function NumberWidget({ descriptor, formState, setFormState }) {
    const {
        key, label, min, max, fallback, tooltip, widgetProps,
    } = descriptor;
    // Cast here, at the source, rather than leaving formState[key] a raw
    // string for every downstream consumer (bounds checks, deriveFormValues,
    // a job's own buildRequestPayload) to re-cast defensively. `''` (the
    // field cleared) is passed through as-is rather than `Number('')`'s
    // `0` -- that would make "blank" indistinguishable from an explicit 0,
    // exactly the bug isEmpty exists to avoid elsewhere in this pipeline.
    // The +/-  buttons already call setValue with a number, so this is a
    // no-op for them; only typed input ever produces the raw string.
    function setNumberValue(value) {
        setFormState({ [key]: value === '' ? value : Number(value) });
    }
    return (
        <Box sx={{ width: '50%', marginTop: '0.5rem' }}>
            <InputLabel htmlFor={`field-${key}`}>{label}</InputLabel>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Box sx={{ flex: 1 }}>
                    <IncrementDecrementInput
                        name={key}
                        min={min}
                        max={max}
                        fallbackValue={fallback}
                        value={formState[key] ?? ''}
                        setValue={setNumberValue}
                        incrementInputProps={{ id: `field-${key}`, placeholder: fallback != null ? String(fallback) : undefined }}
                        {...widgetProps}
                    />
                </Box>
                {tooltip && <TooltipInfo title={resolve(tooltip, formState)} />}
            </Box>
        </Box>
    );
}

export function TextWidget({ descriptor, formState, setFormState }) {
    const {
        key, label, title, widgetProps,
    } = descriptor;
    return (
        <Box sx={{ width: '50%', marginTop: '0.5rem' }}>
            <BasicTextField
                labelText={label}
                value={formState[key] || ''}
                title={title}
                required={false}
                // `BasicTextField` hardcodes its own `margin: 10px 0 0 0`
                // internally — harmless stacked, but it throws off vertical
                // alignment with sibling widgets (e.g. `jobTypeSelect`) in
                // a `row`, none of which carry that extra offset. The outer
                // Box's own `marginTop` already provides consistent spacing.
                style={{ margin: 0 }}
                onChange={(evt) => setFormState({ [key]: evt.target.value })}
                {...widgetProps}
            />
        </Box>
    );
}

// `suggestCompounds`/`placeholderText` let a textarea offer the same
// "insert a workspace compound" convenience REINVENT's input box has always
// had, without the job definition itself needing to know where
// `loadedCompounds` comes from — this widget fetches it directly, the same
// way any other app code reaches the workspace.
export function TextareaWidget({ descriptor, formState, setFormState }) {
    const {
        key, widgetProps, suggestCompounds, placeholderText,
    } = descriptor;
    const value = formState[key] || '';
    const compounds = suggestCompounds
        ? App.Workspace.getLoadedCompounds().filter(
            (compound) => !value.includes(compound.getUnnamedSmiles())
        )
        : [];

    function handleCompoundSelect(compound) {
        const newInput = compound.getSmiles();
        setFormState({ [key]: value ? `${value}\n${newInput}` : newInput });
    }

    return (
        <>
            {compounds.length > 0 && (
                <Select
                    label="Add Workspace Compounds"
                    slotProps={{ select: { native: false } }}
                    sx={{ width: '50%', margin: '1rem 0' }}
                >
                    {compounds.map((compound) => (
                        <MenuItem
                            key={compound.smiles}
                            onClick={() => handleCompoundSelect(compound)}
                        >
                            {compound.displayName}
                        </MenuItem>
                    ))}
                </Select>
            )}
            <Box sx={{ marginTop: '1rem' }}>
                {placeholderText && <PlaceHolder $show={value === ''}>{placeholderText}</PlaceHolder>}
                <TextArea
                    multiline
                    minRows={1}
                    value={value}
                    onChange={(evt) => setFormState({ [key]: evt.target.value })}
                    {...widgetProps}
                />
            </Box>
        </>
    );
}

// A compound loaded in the workspace, standing in for the binding pocket it
// defines. Named `pocket` for the broader concept this will eventually grow
// into (a compound, a hotspot, or the current atom selection, unified the
// way `DockingReference` in `data_tools.js` already does elsewhere in the
// app) — but for now this only offers loaded compounds.
export function PocketWidget({ descriptor, formState, setFormState }) {
    const { key, label } = descriptor;
    const loadedCompounds = App.Workspace.getLoadedCompounds();
    if (loadedCompounds.length === 0) return null;
    return (
        <Box sx={{ marginTop: '1rem' }}>
            <Select
                label={label}
                slotProps={{ select: { native: false } }}
                value={formState[key] || ''}
                onChange={(evt) => setFormState({ [key]: evt.target.value })}
                sx={{ width: '50%' }}
            >
                {loadedCompounds.map((compound) => (
                    <MenuItem key={compound.resSpec} value={compound.resSpec}>
                        {compound.displayName}
                    </MenuItem>
                ))}
            </Select>
        </Box>
    );
}

// The escape hatch: a "widget" that's just whatever JSX the descriptor
// carries, rendered as-is. Lets a fields object hold static content (a
// blurb, a link, a footer note) alongside interactive fields, using the
// exact same vocabulary — formState/setFormState are simply unused.
export function ContentWidget({ descriptor }) {
    return descriptor.content ?? null;
}

// The submit button itself, as an ordinary widget — a tab declares it
// (label/tooltip) as a descriptor like any other, and `JobForm` injects
// `disabled`/`tooltip`/`onClick` at render time (the same pattern as
// `jobTypeSelect`'s `jobTypeIds`), since those depend on the whole form's
// state, not anything this one descriptor alone can compute. `tooltip` doubles
// as the *enabled*-state fallback `submitTooltipFromJobForm` uses — see
// `JobForm.jsx`.
export function SubmitWidget({ descriptor }) {
    const {
        label, tooltip, disabled, onClick,
    } = descriptor;
    return (
        <Box sx={{ marginTop: '1rem' }}>
            <BMapsTooltip title={tooltip}>
                {wrapForDisabledTooltip(
                    <DialogPrimaryButton onClick={onClick} style={{ width: '100%' }} disabled={disabled}>
                        {label}
                    </DialogPrimaryButton>
                )}
            </BMapsTooltip>
        </Box>
    );
}

// Which `JobDefinition` to run is genuinely different from an ordinary
// descriptor — it determines which *other* descriptors even apply — but a
// job still declares it as an ordinary widget in its own `jobForm.sections`,
// listing its own `jobTypeIds` alternatives; `JobForm` reads whichever one
// is currently selected out of formState the same way it reads any descriptor.
export function JobTypeSelectWidget({ descriptor, formState, setFormState }) {
    const { key, label, jobTypeIds } = descriptor;
    // No `default` is declared for this descriptor (a job shouldn't have to know
    // which job type comes first), so `formState[key]` stays unset until the
    // user picks something — falling back to `jobTypeIds[0]` here mirrors
    // `JobForm`'s own `chosenJobType` derivation, so the displayed value
    // matches which job's fields are actually showing.
    return (
        <Box sx={{ width: '50%', marginTop: '0.5rem' }}>
            <Select
                label={label}
                slotProps={{ select: { native: false } }}
                value={formState[key] || jobTypeIds[0] || ''}
                onChange={(evt) => setFormState({ [key]: evt.target.value })}
            >
                {jobTypeIds.map((jobTypeId) => (
                    <MenuItem key={jobTypeId} value={jobTypeId}>
                        {lookupJobDefinition(jobTypeId)?.getJobTypeName()}
                    </MenuItem>
                ))}
            </Select>
        </Box>
    );
}

export function SelectWidget({ descriptor, formState, setFormState }) {
    const {
        key, label, tooltip, widgetProps = {},
    } = descriptor;
    const options = descriptor.optionsFrom
        ? descriptor.optionsFrom(formState)
        : (descriptor.options || []);
    const { ComponentProps, ...restWidgetProps } = widgetProps;
    return (
        <SelectWithInfoTooltip
            value={formState[key] ?? ''}
            onChange={(evt) => setFormState({ [key]: evt.target.value })}
            slotProps={{ select: { native: false } }}
            tooltip={resolve(tooltip, formState)}
            ComponentProps={{ label, ...ComponentProps }}
            {...restWidgetProps}
        >
            {options.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
        </SelectWithInfoTooltip>
    );
}

// A value that is genuinely one of a few distinct *kinds*, where picking a
// kind determines what (if anything) else needs to be filled in — e.g.
// "let the tool decide" vs. "a fixed value" vs. "a range". Each option can
// list its own sub-descriptors, rendered by recursing into
// `FieldList`: a kind needing zero, one, or several sub-fields all work the
// same way, since "how many" is just "however many descriptors that option
// lists". Sub-fields live as ordinary top-level `formState` keys alongside
// this widget's own mode key, same as any other field.
export function VariantWidget({ descriptor, formState, setFormState }) {
    const {
        key, label, tooltip, options, widgetProps = {},
    } = descriptor;
    const mode = formState[key] ?? options[0]?.value;
    const current = options.find((option) => option.value === mode);
    const { ComponentProps, ...restWidgetProps } = widgetProps;

    // A mode's own sub-descriptors mean nothing once a different mode is
    // active — same reasoning, and same fix, as `JobForm`'s job-type
    // switch: drop whatever was only relevant to the previous mode rather
    // than leaving it sitting invisible in `formState`. Sub-descriptors
    // are never shared between modes here, so this always fully clears
    // the outgoing mode's own values.
    function handleModeChange(newMode) {
        const outgoingKeys = current?.fields?.map((d) => d.key) || [];
        const incoming = options.find((option) => option.value === newMode);
        const incomingKeys = new Set(incoming?.fields?.map((d) => d.key) || []);
        const patch = { [key]: newMode };
        for (const staleKey of outgoingKeys) {
            if (!incomingKeys.has(staleKey)) patch[staleKey] = undefined;
        }
        setFormState(patch);
    }

    return (
        <Box sx={{ marginTop: '1rem' }}>
            <SelectWithInfoTooltip
                value={mode}
                onChange={(evt) => handleModeChange(evt.target.value)}
                slotProps={{ select: { native: false } }}
                tooltip={resolve(tooltip, formState)}
                ComponentProps={{ label, ...ComponentProps }}
                // `withTooltip` clips its own children with `overflow:
                // hidden` (to suppress a scrollbar from inline error text) —
                // but an outlined-variant floating label sits a few px above
                // its own box once shrunk, and that same boundary clips it
                // when there's no padding above it in here. Margin on an
                // ancestor (what was here before) can't fix this: it moves
                // the whole already-clipped unit, not the clip boundary.
                ContainerProps={{ style: { paddingTop: '0.75rem' } }}
                {...restWidgetProps}
            >
                {options.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
            </SelectWithInfoTooltip>
            {current?.fields?.length > 0 && (
                <Box sx={{
                    marginTop: '0.5rem', marginLeft: '0.5rem', paddingLeft: '1rem', borderLeft: '2px solid', borderColor: 'divider',
                }}
                >
                    <FieldList
                        descriptors={current.fields}
                        formState={formState}
                        setFormState={setFormState}
                    />
                </Box>
            )}
        </Box>
    );
}

export function CheckboxWidget({ descriptor, formState, setFormState }) {
    const {
        key, label, tooltip, widgetProps = {},
    } = descriptor;
    const checked = descriptor.displayValue
        ? descriptor.displayValue(formState)
        : !!formState[key];
    const disabled = descriptor.disabled ? resolve(descriptor.disabled, formState) : false;
    const { ComponentProps, ...restWidgetProps } = widgetProps;
    return (
        <CheckboxWithInfoTooltip
            checked={checked}
            disabled={disabled}
            onChange={(evt) => setFormState({ [key]: evt.target.checked })}
            tooltip={resolve(tooltip, formState)}
            ComponentProps={{ label, ...ComponentProps }}
            {...restWidgetProps}
        />
    );
}
